import React, { useRef, useState } from 'react';
import { MinusSmIcon, PlusSmIcon } from '@heroicons/react/solid';
import {
  TIME_TRIGGER_OFFSET_MAX,
  TIME_TRIGGER_TIMING_OPTIONS,
} from './automationMetadata';
import type { TimeTriggerTiming } from './automationMetadata';
import { pluralize } from '../../lib/copy';

interface OffsetControlProps {
  offset: number;
  onChange: (offset: number) => void;
  anchorLabel: string;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

const getTimingFromOffset = (offset: number): TimeTriggerTiming => {
  if (!Number.isFinite(offset)) return 'on_date';
  if (offset < 0) return 'before';
  if (offset > 0) return 'after';
  return 'on_date';
};

const OffsetControl: React.FC<OffsetControlProps> = ({
  offset,
  onChange,
  anchorLabel,
}) => {
  const safeOffset = Number.isFinite(offset)
    ? clamp(offset, -TIME_TRIGGER_OFFSET_MAX, TIME_TRIGGER_OFFSET_MAX)
    : 0;
  const timing = getTimingFromOffset(safeOffset);
  const absDays = safeOffset === 0 ? 1 : Math.abs(safeOffset);

  const [draft, setDraft] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const applySign = (days: number): number =>
    timing === 'before' ? -days : days;

  const resolvedDays = (() => {
    if (draft === null) return absDays;
    const raw = parseInt(draft, 10);
    return clamp(isNaN(raw) ? absDays : raw, 1, TIME_TRIGGER_OFFSET_MAX);
  })();

  const displayValue = draft !== null ? draft : absDays;

  const handleTimingChange = (newTiming: TimeTriggerTiming) => {
    const base = resolvedDays;
    setDraft(null);
    if (newTiming === 'on_date') {
      onChange(0);
    } else if (newTiming === 'before') {
      onChange(-base);
    } else {
      onChange(base);
    }
  };

  const commitInput = () => {
    if (draft === null) return;
    const raw = parseInt(draft, 10);
    const clamped = clamp(
      isNaN(raw) ? absDays : raw,
      1,
      TIME_TRIGGER_OFFSET_MAX,
    );
    setDraft(null);
    onChange(applySign(clamped));
  };

  const previewText = () => {
    if (safeOffset === 0) return `on the ${anchorLabel}`;
    if (safeOffset < 0)
      return `${pluralize(absDays, 'day', 'days')} before the ${anchorLabel}`;
    return `${pluralize(absDays, 'day', 'days')} after the ${anchorLabel}`;
  };

  return (
    <div className="flex flex-col gap-3">
      <div>
        <label className="text-xs text-neutral-130 mb-1 block font-semibold">
          Timing
        </label>
        <div className="flex space-x-1 rounded-xl bg-neutral-50 p-1">
          {TIME_TRIGGER_TIMING_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`text-sm rounded-lg px-3 py-1.5 font-medium focus:outline-none focus:ring-2 focus:ring-secondary-50 ${
                timing === option.value
                  ? 'bg-white text-green-150 shadow'
                  : 'text-neutral-125 hover:text-green-150'
              }`}
              onClick={() => handleTimingChange(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {timing !== 'on_date' && (
        <div className="flex flex-col gap-1.5">
          <label className="text-xs text-neutral-130 block font-semibold">
            Days {timing === 'before' ? 'before' : 'after'}
          </label>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setDraft(null);
                onChange(applySign(resolvedDays - 1));
              }}
              disabled={resolvedDays <= 1}
              className="text-neutral-130 flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-neutral-100 hover:bg-neutral-50 disabled:opacity-40 disabled:hover:bg-transparent"
              aria-label="Decrease days"
            >
              <MinusSmIcon className="h-4 w-4" />
            </button>
            <input
              ref={inputRef}
              type="number"
              min={1}
              max={TIME_TRIGGER_OFFSET_MAX}
              value={displayValue}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commitInput}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitInput();
              }}
              className="text-sm w-20 rounded-md border border-neutral-100 bg-white px-3 py-1.5 text-center text-body focus:border-secondary-100 focus:outline-none focus:ring-2 focus:ring-secondary-50"
            />
            <button
              type="button"
              onClick={() => {
                const next = Math.min(
                  resolvedDays + 1,
                  TIME_TRIGGER_OFFSET_MAX,
                );
                setDraft(null);
                onChange(applySign(next));
              }}
              disabled={resolvedDays >= TIME_TRIGGER_OFFSET_MAX}
              className="text-neutral-130 flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-neutral-100 hover:bg-neutral-50 disabled:opacity-40 disabled:hover:bg-transparent"
              aria-label="Increase days"
            >
              <PlusSmIcon className="h-4 w-4" />
            </button>
          </div>
          <div className="flex justify-center">
            <span className="text-xs rounded-full bg-secondary-25 px-2.5 py-0.5 font-semibold text-green-150">
              {pluralize(absDays, 'day', 'days')} {timing}
            </span>
          </div>
        </div>
      )}

      <div className="bg-secondary-5 flex items-center gap-2 rounded-lg border border-secondary-25 p-2.5">
        <span className="text-green-100">{'\u2713'}</span>
        <span className="text-sm text-green-150">
          Triggers <strong>{previewText()}</strong>
        </span>
      </div>
    </div>
  );
};

export default OffsetControl;
